import {
  useCallback,
  ReactNode,
  FocusEventHandler,
  FocusEvent,
  ChangeEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  useFormContext,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { transformValidationOptions } from '../../../helpers/shared/transformValidationOptions';
import { SelectChangeEvent } from '@mui/material/Select/SelectInput';

type SelectChangeEventType =
  | SelectChangeEvent<unknown>
  | ChangeEvent<HTMLSelectElement>;

export interface UseSelectFieldControllerProps<TFieldValues>
  extends UseControllerProps<TFieldValues> {
  onChange?: (event: SelectChangeEventType, child: ReactNode) => void;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

export function useSelectFieldController<TFieldValues>(
  props: UseSelectFieldControllerProps<TFieldValues>,
) {
  const { name, onChange, onBlur, rules, ...selectFieldControllerProps } =
    props;

  const { t } = useTranslation();
  const { control, getValues } = useFormContext<TFieldValues>();

  const selectFieldRules = transformValidationOptions<TFieldValues>(
    t,
    rules,
    getValues,
  );
  const required = typeof selectFieldRules.required !== 'undefined';

  const controllerProps = useController<TFieldValues>({
    name,
    control,
    rules: selectFieldRules,
    ...selectFieldControllerProps,
  });
  const { field } = controllerProps;

  const handleChange = useCallback(
    (event: SelectChangeEventType, child: ReactNode) => {
      field.onChange(event);

      if (onChange) {
        onChange(event, child);
      }
    },
    [field, onChange],
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      field.onBlur();

      if (onBlur) {
        onBlur(event);
      }
    },
    [field, onBlur],
  );

  return {
    required,
    onChangeField: handleChange,
    onBlurField: handleBlur,
    ...controllerProps,
  };
}
